import React, { useState, useRef, useEffect, useCallback } from 'react';
import { hapticFeedback } from '../../../utils/telegram';
import './SwipeToGetFoxButton.css';

const SwipeToGetFoxButton = ({ onGetFox, disabled = false, customText = null }) => {
  const [swipeProgress, setSwipeProgress] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isBouncing, setIsBouncing] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasReachedThreshold, setHasReachedThreshold] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  const containerRef = useRef(null);
  const sliderRef = useRef(null);
  const startXRef = useRef(0);
  const currentXRef = useRef(0);
  const animationFrameRef = useRef(null);
  const lastVibrationProgressRef = useRef(0);

  const SWIPE_THRESHOLD = 0.9; // 80% для активации
  const SLIDER_WIDTH = 52; // Ширина слайдера (соответствует CSS)
  const VIBRATION_INTERVAL = 0.1; // Вибрация каждые 10% прогресса

  // Default texts
  const defaultTexts = {
    default: 'Проведите для получения Fox',
    threshold: 'Отпустите для получения',
    processing: 'Получение Fox...',
    completed: 'Получено 55 Fox'
  };

  // Use custom texts if provided, otherwise use defaults
  const texts = customText || defaultTexts;

  // Функция для создания прогрессивной вибрации
  const triggerProgressiveVibration = (progress) => {
    try {
      if (progress < 0.3) {
        hapticFeedback.light();
      } else if (progress < 0.6) {
        hapticFeedback.medium();
      } else {
        hapticFeedback.heavy();
      }
    } catch (error) {
      console.log('Progressive vibration not available:', error);
    }
  };

  // Функция для финальной мощной вибрации
  const triggerThresholdVibration = () => {
    try {
      hapticFeedback.heavy();
    } catch (error) {
      console.log('Threshold vibration not available:', error);
    }
  };

  // Функция для вибрации успеха
  const triggerSuccessVibration = () => {
    try {
      hapticFeedback.success();
    } catch (error) {
      console.log('Success vibration not available:', error);
    }
  };

  // Функция для сброса состояния компонента
  const resetProgress = () => {
    setSwipeProgress(0);
    setIsCompleted(false);
    setIsDragging(false);
    setIsBouncing(false);
    setIsProcessing(false);
    setHasReachedThreshold(false);
    setShowConfetti(false);
  };

  // Оптимизированное обновление прогресса
  const updateProgress = useCallback((newProgress) => {
    // Округляем до 2 знаков после запятой для уменьшения количества обновлений
    const roundedProgress = Math.round(newProgress * 100) / 100;
    
    // Обновляем только если есть значимое изменение
    if (Math.abs(roundedProgress - swipeProgress) > 0.01) {
      setSwipeProgress(roundedProgress);
    }
  }, [swipeProgress]);

  useEffect(() => {
    if (!containerRef.current || disabled) return;

    const container = containerRef.current;
    const slider = sliderRef.current;

    const handleStart = (e) => {
      if (isCompleted) return;

      const clientX = e.type === 'mousedown' ? e.clientX : e.touches[0].clientX;
      startXRef.current = clientX;
      currentXRef.current = clientX;
      setIsDragging(true);
      setHasReachedThreshold(false);
      lastVibrationProgressRef.current = 0;

      // Haptic feedback при начале свайпа
      hapticFeedback.light();
    };

    const handleMove = (e) => {
      if (!isDragging || isCompleted) return;

      e.preventDefault();
      const clientX = e.type === 'mousemove' ? e.clientX : e.touches[0].clientX;
      
      // Проверяем, есть ли значимое изменение позиции
      if (Math.abs(clientX - currentXRef.current) < 2) return;
      
      const containerRect = container.getBoundingClientRect();
      const maxDistance = containerRect.width - SLIDER_WIDTH;

      const deltaX = clientX - startXRef.current;
      const progress = Math.max(0, Math.min(1, deltaX / maxDistance));

      updateProgress(progress);
      currentXRef.current = clientX;

      // Прогрессивная вибрация во время свайпа каждые 10% прогресса
      const progressSteps = Math.floor(progress / VIBRATION_INTERVAL);
      const lastProgressSteps = Math.floor(lastVibrationProgressRef.current / VIBRATION_INTERVAL);
      
      if (progressSteps > lastProgressSteps && progress > 0.1) {
        triggerProgressiveVibration(progress);
        lastVibrationProgressRef.current = progress;
      }

      // Мощная вибрация при достижении порога (только один раз)
      if (progress >= SWIPE_THRESHOLD && !hasReachedThreshold) {
        setHasReachedThreshold(true);
        triggerThresholdVibration();
      } else if (progress < SWIPE_THRESHOLD && hasReachedThreshold) {
        setHasReachedThreshold(false);
      }
    };

    const handleEnd = () => {
      if (!isDragging || isCompleted || isProcessing) return;

      setIsDragging(false);

      if (swipeProgress >= SWIPE_THRESHOLD) {
        // Закрепляем кнопку справа и начинаем обработку
        setSwipeProgress(1);
        setIsProcessing(true);

        // Вибрация успеха
        triggerSuccessVibration();

        // Имитация обработки получения Fox токенов
        setTimeout(() => {
          setIsProcessing(false);
          setIsCompleted(true);
          setShowConfetti(true);
          hapticFeedback.success();

          // Убираем конфети через 3 секунды
          setTimeout(() => {
            setShowConfetti(false);
          }, 3000);

          // Вызываем callback через небольшую задержку после анимации
          setTimeout(() => {
            onGetFox();
            // Сбрасываем прогресс после закрытия окна (через небольшую задержку)
            setTimeout(() => {
              resetProgress();
            }, 500);
          }, 1000);
        }, 1500); // 1.5 секунды обработки
      } else {
        // Возвращаем в исходное положение с отскакиванием
        setIsBouncing(true);
        setSwipeProgress(0);
        setHasReachedThreshold(false);

        // Убираем класс bouncing после завершения анимации
        setTimeout(() => {
          setIsBouncing(false);
          hapticFeedback.medium();
        }, 600);
      }
    };

    // Mouse events
    slider.addEventListener('mousedown', handleStart);
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleEnd);

    // Touch events
    slider.addEventListener('touchstart', handleStart, { passive: false });
    document.addEventListener('touchmove', handleMove, { passive: false });
    document.addEventListener('touchend', handleEnd);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      slider.removeEventListener('mousedown', handleStart);
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleEnd);

      slider.removeEventListener('touchstart', handleStart);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleEnd);
    };
  }, [isDragging, swipeProgress, isCompleted, disabled, onGetFox, updateProgress]);

  const containerWidth = containerRef.current?.offsetWidth || 300; // Фиксированная ширина по умолчанию
  const maxDistance = containerWidth - SLIDER_WIDTH;
  
  const sliderPosition = (isProcessing || isCompleted) ?
    maxDistance :
    (isBouncing ? 0 : swipeProgress * maxDistance);

  // Синхронизированный расчет фона - правый край фона совпадает с правым краем слайдера
  const backgroundWidth = sliderPosition + SLIDER_WIDTH; // Ширина до правого края слайдера

  // Создаем строку классов
  const containerClasses = [
    'swipe-to-fox',
    disabled && 'disabled',
    isCompleted && 'completed',
    isProcessing && 'processing',
    isDragging && 'dragging',
    isBouncing && 'bouncing'
  ].filter(Boolean).join(' ');

  return (
    <div
      ref={containerRef}
      className={containerClasses}
    >
      {/* Фоновый градиент */}
      <div
        className="swipe-background"
        style={{
          width: isBouncing ? `${SLIDER_WIDTH}px` : `${backgroundWidth}px`,
          transition: isDragging ? 'none' : isBouncing ? 'width 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55)' : 'width 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
        }}
      />

      {/* Анимация конфети из слайдера */}
      {showConfetti && (
                <div 
          className="confetti-container"
          style={{
            left: `${sliderPosition - 2}px`, // Начало слайдера (учитываем left: -2px)
            top: `2px`, // Верх слайдера
            width: `${SLIDER_WIDTH}px`, // Вся ширина слайдера
            height: `${SLIDER_WIDTH}px` // Вся высота слайдера
          }}
        >
          {[...Array(15)].map((_, i) => {
            const randomX = (Math.random() - 0.5) * 120; // Больше разброс по X
            const randomStartX = Math.random() * SLIDER_WIDTH; // Случайная позиция внутри слайдера
            return (
              <div
                key={i}
                className="confetti"
                style={{
                  left: `${randomStartX}px`, // Случайная позиция внутри слайдера
                  '--random-x': `${randomX}px`
                }}
              ></div>
            );
          })}
        </div>
      )}

      {/* Текст */}
      <div className="swipe-text">
        {isCompleted ? (
          <span className="completed-text">
            {texts.completed}
          </span>
        ) : isProcessing ? (
          <span className="processing-text">
            <div className="processing-spinner"></div> {texts.processing}
          </span>
        ) : (
          <span>
            {swipeProgress > SWIPE_THRESHOLD ? texts.threshold : texts.default}
          </span>
        )}
      </div>

      {/* Слайдер */}
      <div
        ref={sliderRef}
        className="swipe-slider"
        style={{
          transform: isBouncing ? 'translateX(0px)' : `translateX(${sliderPosition}px)`,
          transition: isDragging ? 'none' : isBouncing ? 'transform 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55)' : 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
        }}
      >
        {isCompleted ? (
          <span className="slider-icon completed">-</span>
        ) : isProcessing ? (
          <div className="spinner"></div>
        ) : (
          <span className="slider-icon">→</span>
        )}
      </div>
    </div>
  );
};

export default SwipeToGetFoxButton; 